import React, { useState, useEffect } from 'react';
import CommonDataTable from '../../Common/CommonDataTable';
import LoadingSpinner from '../../Common/LoadingSpinner';

const DebitNoteAllocationGrid = ({
    supplier,
    debitNoteAllocations = [],
    onDebitNoteAllocationsChange,
    errors = {},
    loading = false,
    onLoadAvailableDebitNotes
}) => {
    const [availableDebitNotes, setAvailableDebitNotes] = useState([]);
    const [loadingDebitNotes, setLoadingDebitNotes] = useState(false);
    const [showAddDebitNote, setShowAddDebitNote] = useState(false);

    // Load available debit notes when supplier changes
    useEffect(() => {
        if (supplier && onLoadAvailableDebitNotes) {
            loadAvailableDebitNotes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [supplier]);

    const loadAvailableDebitNotes = async () => {
        if (!supplier || !onLoadAvailableDebitNotes) return;

        setLoadingDebitNotes(true);
        try {
            const notes = await onLoadAvailableDebitNotes(supplier);
            setAvailableDebitNotes(notes);
        } catch (error) {
            console.error('Error loading available debit notes:', error);
        } finally {
            setLoadingDebitNotes(false);
        }
    };

    const handleAmountChange = (index, field, value) => {
        const updated = [...debitNoteAllocations];
        updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
        if (field === 'amountSetoff') {
            updated[index].remainingBalance = updated[index].setoffAmount - (parseFloat(value) || 0);
        }
        onDebitNoteAllocationsChange(updated);
    };

    const handleNotesChange = (index, notes) => {
        const updated = [...debitNoteAllocations];
        updated[index] = { ...updated[index], notes };
        onDebitNoteAllocationsChange(updated);
    };

    const handleRemove = (index) => {
        onDebitNoteAllocationsChange(debitNoteAllocations.filter((_, i) => i !== index));
    };

    const handleAdd = (dn) => {
        const available = dn.paymentSetoff?.remainingAmount || dn.totals?.totalWithTax || 0;
        const newAllocation = {
            debitNoteId: dn._id,
            debitNoteNo: dn.debitNoteNumber,
            debitDate: dn.debitDate,
            noteValue: dn.totals?.totalWithTax || 0,
            setoffAmount: available,
            amountSetoff: 0,
            remainingBalance: available,
            notes: ''
        };
        onDebitNoteAllocationsChange([...debitNoteAllocations, newAllocation]);
        setShowAddDebitNote(false);
    };

    const formatDate = (date) => (date ? new Date(date).toLocaleDateString() : '-');

    const columns = [
        { key: 'debitNoteNo', header: 'Debit Note No.', width: '140px' },
        { key: 'debitDate', header: 'Date', width: '100px', render: (value) => formatDate(value) },
        { key: 'noteValue', header: 'Note Value', width: '120px', render: (value) => parseFloat(value || 0).toFixed(2) },
        { key: 'setoffAmount', header: 'Available Setoff', width: '140px', render: (value) => parseFloat(value || 0).toFixed(2) },
        {
            key: 'amountSetoff',
            header: 'Amount Setoff',
            width: '140px',
            render: (value, row, index) => (
                <input
                    type="number"
                    value={value === 0 ? '0' : (value || '')}
                    onChange={(e) => handleAmountChange(index, 'amountSetoff', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    min="0"
                    max={row.setoffAmount}
                    step="0.01"
                />
            )
        },
        {
            key: 'remainingBalance',
            header: 'Remaining Balance',
            width: '140px',
            render: (value) => (
                <span className={`font-medium ${value > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {parseFloat(value || 0).toFixed(2)}
                </span>
            )
        },
        {
            key: 'notes',
            header: 'Notes',
            render: (value, row, index) => (
                <input
                    type="text"
                    value={value || ''}
                    onChange={(e) => handleNotesChange(index, e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add notes..."
                />
            )
        }
    ];

    const availableColumns = [
        { key: 'debitNoteNumber', header: 'Debit Note No.', width: '140px' },
        { key: 'debitDate', header: 'Date', width: '100px', render: (value) => formatDate(value) },
        { header: 'Note Value', width: '120px', render: (value, row) => parseFloat(row?.totals?.totalWithTax || 0).toFixed(2) },
        { header: 'Available Setoff', width: '140px', render: (value, row) => parseFloat(row?.paymentSetoff?.remainingAmount || row?.totals?.totalWithTax || 0).toFixed(2) }
    ];

    const filteredAvailable = availableDebitNotes.filter(dn =>
        !debitNoteAllocations.some(a => a.debitNoteId === dn._id)
    );

    const totalAmountSetoff = debitNoteAllocations.reduce((sum, a) => sum + (a.amountSetoff || 0), 0);

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between bg-gradient-to-r from-amber-600 to-orange-600 px-3 py-1.5">
                <span className="text-xs font-semibold text-white">Debit Note Allocation</span>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-amber-100">Total: {parseFloat(totalAmountSetoff).toFixed(2)}</span>
                    {supplier && (
                        <button
                            type="button"
                            onClick={() => setShowAddDebitNote(true)}
                            className="flex items-center gap-1 px-2 py-0.5 bg-white/20 hover:bg-white/30 rounded text-xs text-white transition-colors"
                            disabled={loadingDebitNotes}
                        >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Add Debit Note
                        </button>
                    )}
                </div>
            </div>
            <div className="p-4">
                {errors.debitNoteAllocations && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-600">{errors.debitNoteAllocations}</p>
                    </div>
                )}

                {!supplier ? (
                    <div className="text-center py-8 text-gray-500">
                        <p className="text-lg font-medium">Select a supplier first</p>
                        <p className="text-sm">Choose a supplier to load available debit notes for setoff</p>
                    </div>
                ) : debitNoteAllocations.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p className="text-lg font-medium">No debit notes allocated</p>
                        <p className="text-sm">Click "Add Debit Note" to start allocating debit note setoffs</p>
                    </div>
                ) : (
                    <CommonDataTable
                        data={debitNoteAllocations}
                        columns={columns}
                        loading={loading}
                        emptyMessage="No debit note allocations"
                        customActions={[
                            {
                                label: 'Remove',
                                onClick: (row, index) => handleRemove(index),
                                icon: (
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                ),
                                color: 'text-red-600 hover:text-red-800'
                            }
                        ]}
                    />
                )}

                {/* Add Debit Note Modal */}
                {showAddDebitNote && (
                    <div className="fixed inset-0 backdrop-blur-sm bg-black/20 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
                        <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl border border-gray-100 max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-orange-600 rounded-t-xl px-6 py-4 z-10">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold text-white">Add Available Debit Notes</h3>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddDebitNote(false)}
                                        className="w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 transition-all"
                                    >
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="p-6">
                                {loadingDebitNotes ? (
                                    <div className="flex items-center justify-center py-8">
                                        <LoadingSpinner />
                                        <span className="ml-2 text-gray-600">Loading available debit notes...</span>
                                    </div>
                                ) : filteredAvailable.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        <p className="text-lg font-medium">No available debit notes</p>
                                        <p className="text-sm">All debit notes for this supplier are already allocated or used</p>
                                    </div>
                                ) : (
                                    <CommonDataTable
                                        data={filteredAvailable}
                                        columns={availableColumns}
                                        emptyMessage="No available debit notes"
                                        onRowClick={handleAdd}
                                        customActions={[
                                            {
                                                label: 'Add',
                                                onClick: handleAdd,
                                                icon: (
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                    </svg>
                                                ),
                                                color: 'text-green-600 hover:text-green-800'
                                            }
                                        ]}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DebitNoteAllocationGrid;
